"use client";

import { apiSubscribeNewsletter } from "@/apis/page_optimization";
import { apiVerifyCaptcha } from "@/apis/captcha";
import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

interface Props {
    companyId: string;
    domain: string;
}

const NewsletterSection = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState<string>("");
    const [status, setStatus] = useState<
        "idle" | "verifying" | "sending" | "failed"
    >("idle");

    const { executeRecaptcha } = useGoogleReCaptcha();

    const buttonLabel = {
        idle: "Subscribe",
        verifying: "Verifying...",
        sending: "Subscribing...",
        failed: "Failed",
    }[status];

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailStr);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedEmail = email.trim();

        // Validate empty email
        if (!trimmedEmail) {
            toast.error("Email field is required.", {
                autoClose: 2000,
            });
            return;
        }

        // Validate email format
        if (!validateEmail(trimmedEmail)) {
            toast.error("Please enter a valid email address.", {
                autoClose: 2000,
            });
            return;
        }

        // Check reCAPTCHA availability
        if (!executeRecaptcha) {
            toast.error("reCAPTCHA not ready. Please try again.", {
                autoClose: 2000,
            });
            return;
        }

        try {
            // Step 1: Verify reCAPTCHA
            setStatus("verifying");

            const recaptchaToken = await executeRecaptcha("newsletter_form");

            const captchaRes = await apiVerifyCaptcha(
                domain,
                recaptchaToken
            );

            if (!captchaRes?.verified) {
                setStatus("failed");

                toast.error(
                    "Captcha verification failed. Please try again.",
                    {
                        autoClose: 2000,
                    }
                );

                return;
            }

            // Step 2: Subscribe newsletter
            setStatus("sending");

            const response = await apiSubscribeNewsletter(
                companyId,
                trimmedEmail
            );

            if (
                response?.status === 200 ||
                response?.status === true ||
                response?.message === "Subscribed successfully"
            ) {
                toast.success("Thank you for subscribing!", {
                    autoClose: 2000,
                });

                setEmail("");
            } else {
                toast.error(
                    response?.message ||
                    "You are already subscribed to our newsletter.",
                    {
                        autoClose: 2000,
                    }
                );
            }
        } catch (error) {
            console.error("Error subscribing:", error);

            toast.error(
                "Failed to subscribe. Please try again later.",
                {
                    autoClose: 2000,
                }
            );

            setStatus("failed");
        } finally {
            setStatus("idle");
        }
    };

    return (
        <section className="newsletter-section py-5 px-3 px-md-5 container-fluid">
            <ToastContainer />

            <div className="text-center mb-4">
                <h2 className="newsletter-title">
                    Stay Updated
                </h2>

                <p className="newsletter-subtitle opacity-80 f-25">
                    Subscribe to our newsletter and never miss out on the
                    latest deals & updates.
                </p>
            </div>

            <form
                className="newsletter-form row justify-content-center g-3"
                onSubmit={handleSubmit}
                autoComplete="off"
            >
                <div className="col-12 col-md-7 col-lg-6">
                    <input
                        type="email"
                        placeholder="Enter your email address"
                        className="newsletter-input w-100"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status !== "idle"}
                    />
                </div>

                <div className="col-12 col-md-3 col-lg-3">
                    <button
                        type="submit"
                        className="newsletter-btn w-100"
                        disabled={status !== "idle"}
                    >
                        {buttonLabel}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default NewsletterSection;